import { FieldValueLookupError } from '@atlassian/jira-issue-adjustments/src/common/errors/field-value-lookup-error.tsx';
import type { OptionsVisibility } from '@atlassian/jira-issue-adjustments/src/common/types/options-visibility.tsx';
import type { IssueTypePublicSetValue } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/fields/issue-type.tsx';
import { applyOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/apply-options-visibility-rule/index.tsx';
import { getAvailableOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/get-available-options-visibility-rule/index.tsx';
import { singleValueLookup } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/utils/single-value-lookup/index.tsx';

export type IssueTypeInternalValue = {
	iconUrl: string;
	label: string;
	value: string;
};

export type IssueTypePublicGetValue = {
	id: string;
	name: string;
} | null;

export type IssueTypeLookupValue = {
	value: string;
	label: string;
	iconUrl: string;
};

export const issueTypeSetValueTransformer = (
	value: IssueTypePublicSetValue,
	lookupValues?: IssueTypeLookupValue[],
): IssueTypeInternalValue => {
	const lookupValue = singleValueLookup<IssueTypeLookupValue>(value, lookupValues, 'value');

	if (!lookupValue) {
		throw new FieldValueLookupError();
	}

	return {
		iconUrl: lookupValue.iconUrl,
		label: lookupValue.label,
		value: lookupValue.value,
	};
};

export const issueTypeGetValueTransformer = (
	value: IssueTypeInternalValue | null,
): IssueTypePublicGetValue => {
	/**
	 * Switching between projects with UIM context doesn't reset the IA context in a timely
	 * manner, that's why we still have `adjustmentsAreEnabled` resulting in `true` even
	 * though the context should have been reset and the undecorated component should have rendered first.
	 * As a result, issue type field sends `null` as a value to the value decorator just
	 * before the context reset is being triggered.
	 * `null` won't be sent to jira-bridge because after the context is reset, the actual issue type option is being set.
	 */
	if (!value) {
		return null;
	}

	return {
		id: value.value,
		name: value.label,
	};
};

export const transformIssueTypeSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: IssueTypeLookupValue[],
): IssueTypeLookupValue[] => applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'value');

export const transformIssueTypeGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: IssueTypeLookupValue[],
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'value');
